import { Geolocation, Position, PositionOptions } from '@capacitor/geolocation'
import {
    LiveLocation,
    LiveLocationPayload,
    LiveLocationSchema,
    spLog,
    Tiers,
} from '@eventr/common'
import { io, Socket } from 'socket.io-client'
import z from 'zod'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { DEVTOOLS_ENABLED } from '../../consts'
import { isConnectivityAvailable } from '../../feat/connectivity/connectivityListener'
import { gatewayConfig } from '../../feat/gatewayClient/config'
import { useGuestStore } from '../../feat/guest/guest.store'
import { getClaimsJWT } from '../../feat/oauth/oauth.slice'
import { bs } from '../bound.store'

export const ALLOWED_TIERS = [
    Tiers.enterprise.id,
    Tiers.free.id,
    Tiers.enterpriseFree.id,
    Tiers.pro.id,
]
const positionOptions: PositionOptions = {
    enableHighAccuracy: true,
}
const JoinResponseSchema = z.record(LiveLocationSchema)
const PartialResponseSchema = z.object({
    userId: z.string(),
    liveLocation: LiveLocationSchema,
})

interface LiveLocationState {
    liveLocationSocket: Socket
    liveLocations: Map<string, LiveLocation>
    sharingStaleTimestamp: number | undefined
    geolocationWatchId: string | undefined
    userPosition: Position | undefined
    userLocationDotClientRequired: boolean
}

interface LiveLocationActions {
    shareLiveLocation: (timeIntervalUntilStale: number) => void
    stopSharingLiveLocation: () => void
    joinLiveLocationRoom: () => void
    leaveLiveLocationRoom: () => void
    init(): void
}
export const shareIntervals = [
    {
        displayName: '15 Minutes',
        id: 15 * 60 * 1000,
    },
    {
        displayName: '1 Hour',
        id: 1 * 60 * 60 * 1000,
    },
    {
        displayName: '8 Hours',
        id: 8 * 60 * 60 * 1000,
    },
    {
        displayName: 'Indefinitely',
        id: 100 * 24 * 60 * 60 * 1000,
    },
]
interface LiveLocationClientStore
    extends LiveLocationState,
        LiveLocationActions {}

export const useLiveLocationStore = create<LiveLocationClientStore>()(
    subscribeWithSelector(
        persist(
            devtools(
                (set, get) => {
                    spLog('Creating live location store')

                    async function shareLiveLocation(
                        timeIntervalUntilStale: number
                    ) {
                        spLog('Sharing live location', timeIntervalUntilStale)
                        const staleTimestamp =
                            Date.now() + timeIntervalUntilStale
                        set({ sharingStaleTimestamp: staleTimestamp })
                    }

                    function stopSharingLiveLocation() {
                        spLog('Stopping sharing live location')

                        const { liveLocationSocket } = get()

                        function stopSharingHandler(response: any) {
                            if (response) {
                                set({
                                    sharingStaleTimestamp: undefined,
                                })
                                spLog('Stopped sharing live location.')
                            } else {
                                spLog('Error stopping sharing live location.')
                            }
                        }
                        try {
                            liveLocationSocket?.emit(
                                'stop-sharing',
                                stopSharingHandler
                            )
                        } catch (error) {
                            console.error(
                                `Error stopping sharing live location: ${error}`
                            )
                        }
                    }

                    /**
                     * Join the live location room, which will pull the live locations of all users in the organisation.
                     */
                    async function joinLiveLocationRoom() {
                        spLog('Joining live location room')

                        // Get the live location socket from the store.
                        const { liveLocationSocket } = get()

                        const response = await bs().refreshTokens()

                        if (response.status === 'success') {
                            liveLocationSocket?.disconnect()
                            liveLocationSocket?.connect()
                        }

                        /**
                         * Handles the response from the socket when sending a 'join' event
                         * @param response The response from the socket.
                         */
                        function joinResponseHandler(response: any): void {
                            try {
                                // Parse the response from the socket.
                                const liveLocations =
                                    JoinResponseSchema.parse(response)

                                // Set the live locations in the store.
                                set({
                                    liveLocations: new Map(
                                        Object.entries(liveLocations)
                                    ),
                                })
                            } catch (error) {
                                console.error(
                                    `Error parsing join response: ${error}`
                                )
                            }
                        }

                        // Emit the 'join' event to the socket.
                        liveLocationSocket?.emit('join', joinResponseHandler)
                    }

                    /**
                     * Leave the live location room.
                     */
                    function leaveLiveLocationRoom() {
                        spLog('Leaving live location room')

                        const { liveLocationSocket } = get()

                        /**
                         * Handles the response from the socket when sending a 'leave' event.
                         * @param response The response from the socket.
                         */
                        function leaveResponseHandler(response: any) {
                            if (response) {
                                spLog('Left live location room.')
                            } else {
                                spLog('Error leaving live location room.')
                            }
                        }

                        // Emit the 'leave' event to the socket.
                        liveLocationSocket?.emit('leave', leaveResponseHandler)
                    }

                    function init() {
                        const { liveLocationSocket } = get()
                        if (liveLocationSocket?.connected) {
                            if (liveLocationSocket?.disconnect)
                                liveLocationSocket?.disconnect()
                        }
                        if (
                            bs().orgConfig.disableLocationServices === true || // Organisation has location services disabled
                            !ALLOWED_TIERS.includes(bs().tier ?? '') || // Live Location disabled for this tier
                            useGuestStore.getState().isGuestMode || // Live location disabled in Guest mode
                            isConnectivityAvailable.value === false // No internet connection
                        ) {
                            spLog('Live location disabled')
                            return
                        }
                        if (liveLocationSocket?.on) {
                            // Connect to the live location socket.
                            liveLocationSocket?.on(
                                'connect',
                                onConnectionListener
                            )
                            liveLocationSocket?.on('partial', onPartialListener)
                            liveLocationSocket?.on(
                                'disconnect',
                                onDisconnectListener
                            )
                            liveLocationSocket?.on('delete', onDeleteListener)
                            liveLocationSocket?.connect()

                            get().joinLiveLocationRoom()
                        }
                    }

                    const liveLocationActions: LiveLocationActions = {
                        shareLiveLocation,
                        stopSharingLiveLocation,
                        joinLiveLocationRoom,
                        leaveLiveLocationRoom,
                        init,
                    }

                    const truncated = gatewayConfig().baseUrl.slice(0, -4)
                    const SOCKET_URL = `${truncated}live-location`

                    const initialLiveLocationState: LiveLocationState = {
                        liveLocationSocket: io(SOCKET_URL, {
                            auth: liveLocationAuthenticationCallback,
                            autoConnect: false,
                        }),
                        liveLocations: new Map(),
                        sharingStaleTimestamp: undefined,
                        geolocationWatchId: undefined,
                        userPosition: undefined,
                        userLocationDotClientRequired: false,
                    }

                    const state = {
                        ...initialLiveLocationState,
                        ...liveLocationActions,
                    }

                    /**
                     * Handles the connection event from the live location socket.
                     */
                    function onConnectionListener() {
                        spLog('Connected to live location server')
                    }

                    /**
                     * Handles the disconnect event from the live location socket.
                     */
                    function onDisconnectListener() {
                        // Clear the live locations in the store.
                        set({ liveLocations: new Map() })
                        spLog('Disconnected from live location server')
                    }

                    /**
                     * Handles the partial response from the live location socket.
                     * @param response The partial response from the socket.
                     */
                    function onPartialListener(response: any) {
                        // spLog('Partial response received')

                        try {
                            // Parse the partial response from the socket.
                            const result = PartialResponseSchema.parse(response)

                            // Update the live locations in the store.
                            set((prev) => {
                                return {
                                    liveLocations: new Map(
                                        prev.liveLocations
                                    ).set(result.userId, result.liveLocation),
                                }
                            })

                            // spLog(get().liveLocations)
                        } catch (error) {
                            console.error(
                                `Error parsing partial response: ${error}`
                            )
                        }
                    }

                    function onDeleteListener(response: any) {
                        spLog('Delete response received')
                        try {
                            const userId = z.string().parse(response)

                            set((prev) => {
                                const liveLocations = new Map(
                                    prev.liveLocations
                                )
                                liveLocations.delete(userId)

                                return { liveLocations }
                            })
                        } catch (error) {
                            console.error(
                                `Error parsing delete response: ${error}`
                            )
                        }
                    }

                    return state
                },
                { enabled: DEVTOOLS_ENABLED }
            ),
            {
                name: 'live-location-store',
                partialize: (state) => ({
                    sharingStaleTimestamp: state.sharingStaleTimestamp,
                }),
            }
        )
    )
)
export const lls = useLiveLocationStore.getState

/**
 * The callback to retrieve bearer and claims tokens required for authentication with the live location socket. Read more: https://socket.io/docs/v4/middlewares/#sending-credentials.
 * @param cb The callback to pass the tokens to the socket.
 */
async function liveLocationAuthenticationCallback(
    cb: (data: object) => void
): Promise<void> {
    spLog('Retrieving tokens for live location socket.')

    const bearerToken = bs().IDtoken
    const claimsToken = getClaimsJWT()

    cb({
        bearerToken,
        claimsToken,
    })
}

useLiveLocationStore.subscribe(
    (state) => [
        state.sharingStaleTimestamp,
        state.userLocationDotClientRequired,
    ],
    async (state: [number, boolean]) => {
        const [sharingStaleTimestamp, userLocationDotClientRequired] = state
        spLog('Live location state changed', state)

        const locationRequired =
            sharingStaleTimestamp !== undefined || userLocationDotClientRequired
        const noExistingWatchPresent =
            useLiveLocationStore.getState().geolocationWatchId === undefined

        if (locationRequired && noExistingWatchPresent) {
            spLog('Starting watch')
            const watchId = await Geolocation.watchPosition(
                positionOptions,
                (position: Position | null) => {
                    if (!position || !position.coords) return

                    spLog('User position updated', position)

                    useLiveLocationStore.setState({ userPosition: position })
                }
            )

            useLiveLocationStore.setState({ geolocationWatchId: watchId })
        }

        if (!locationRequired && !noExistingWatchPresent) {
            spLog('Clearing watch')
            Geolocation.clearWatch({
                id: useLiveLocationStore.getState().geolocationWatchId ?? '0',
            })
            useLiveLocationStore.setState({
                geolocationWatchId: undefined,
                userPosition: undefined,
            })
        }
    },
    {
        equalityFn: shallow,
        fireImmediately: true,
    }
)

useLiveLocationStore.subscribe(
    (state) => [state.sharingStaleTimestamp, state.userPosition],
    (state: [number, Position]) => {
        const [sharingStaleTimestamp, userPosition] = state

        if (
            sharingStaleTimestamp !== undefined &&
            userPosition &&
            userPosition.coords
        ) {
            const payload: LiveLocationPayload = {
                lngLat: {
                    lng: userPosition.coords.longitude,
                    lat: userPosition.coords.latitude,
                },
                staleTimestamp: userPosition.timestamp,
            }

            spLog('Emitting share event', payload)

            useLiveLocationStore
                .getState()
                .liveLocationSocket?.emit('share', payload)
        }

        if (sharingStaleTimestamp < Date.now()) {
            console.log('Live location is stale, stopping sharing')

            lls().stopSharingLiveLocation()
        }
    },
    {
        equalityFn: shallow,
        fireImmediately: true,
    }
)
